import {DOCUMENT} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNewItemData} from '@application/helper/navigation-helper/navigation-new-item-data.interface';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {UnsavedChangesNotifierService} from '@application/helper/unsaved-changes-notifier/unsaved-changes-notifier.service';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {Gauge} from '@domain/machine/gauge';
import {TuftMachine} from '@domain/machine/tuft-machine';
import {Subscription} from '@domain/profile/subscription';
import {TuftProduct} from '@domain/textile-data/tuft-product/tuft-product';
import {WeaveProduct} from '@domain/textile-data/weave-product/weave-product';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {MACHINES, Machines} from '@infrastructure/http/machine/machines.interface';
import {HttpTuftProductsService} from '@infrastructure/http/tuft-product/http-tuft-products.service';
import {WEAVE_PRODUCT, WeaveProducts} from '@infrastructure/http/weave-product/weave-products.interface';
import {NavigationMachineOverviewData} from '@presentation/pages/machine-overview/navigation-machine-overview-data.interface';
import {SelectWeaveProductComponent} from '@presentation/pages/texfab/production-order-lite/add/select-weave-product/select-weave-product.component';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  DialogBuilderFactoryService,
  DialogType,
  FormValidationHelper,
  ObjectUtils,
  SaveType,
  skeletonViewAnimation,
  TabsMenuItem,
  TranslateService
} from '@vdw/angular-component-library';
import {finalize, Observable, takeUntil} from 'rxjs';
import {TextileService} from '../../textile-data-overview/textile.service';
import {TextileDataType} from '../../textile-data-type.enum';
import {OverviewListWeaveProduct} from '../../weave-product/overview/overview-list-weave-product';
import {AddTuftProductViewMode} from './add-tuft-product-view-mode.enum';
import {NavigationAddTuftProductPageData} from './navigation-add-tuft-product-page-data.type';
import {NavigationTuftProductData} from './navigation-tuft-product-data.interface';
import {GeneralForm, OptionalSettingsForm, TuftProductForm} from './tuft-product-form';

@Component({
  templateUrl: './add-tuft-product-page.component.html',
  styleUrls: ['./add-tuft-product-page.component.scss'],
  animations: [skeletonViewAnimation('.form-field-skeleton-wrapper, .button-skeleton-wrapper')]
})
export class AddTuftProductPageComponent extends BaseComponent implements OnInit {
  public addTuftProductForm: TuftProductForm;
  public gauges: Gauge[];
  public isLoadingTuftProduct = true;
  public tuftProductIsLocked = false;
  public tuftProductToSave: TuftProduct;
  public saveButtonTouched = false;
  public maxWidthInCm: number;

  public selectedMenuItem: TabsMenuItem = {
    value: AddTuftProductViewMode.GENERAL,
    translationKey: 'GENERAL.GENERAL'
  };

  public menuItems: TabsMenuItem[] = [
    this.selectedMenuItem,
    {
      value: AddTuftProductViewMode.OPTIONAL_SETTINGS,
      translationKey: 'GENERAL.OPTIONAL_SETTINGS'
    }
  ];

  public readonly texstyleEditPermission = LastModifiedCardUtils.getPermissionToModifyItems('tuftProduct');
  public readonly translationKeyForTuftProducts = 'TEXTILE_DATA.TUFT_PRODUCT.TUFT_PRODUCT';
  public readonly PRIMARY_BACKING_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.weaveProduct.editWeaveProduct.absolutePath;
  public readonly SAVE_TYPE = SaveType;
  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_TUFT_PRODUCT;

  private urlParams: Params;
  private currentSubscription: Subscription;

  private readonly TUFT_PRODUCT_OVERVIEW_PAGE_URL = RouteUtils.paths.texStyle.tuftProduct.absolutePath;
  private readonly DUPLICATE_TUFT_PRODUCT_PAGE_URL = RouteUtils.paths.texStyle.tuftProduct.duplicateTuftProduct.path;
  private readonly EDIT_TUFT_PRODUCT_PAGE_URL = RouteUtils.paths.texStyle.tuftProduct.editTuftProduct.path;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(DOCUMENT) private document: Document,
    @Inject(MACHINES) private readonly machines: Machines,
    @Inject(WEAVE_PRODUCT) private weaveProducts: WeaveProducts,
    public readonly tuftProductsService: HttpTuftProductsService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationHelperService: NavigationHelperService<NavigationAddTuftProductPageData>,
    private readonly textileService: TextileService,
    private readonly unsavedChangesNotifierService: UnsavedChangesNotifierService
  ) {
    super();
  }

  public get generalForm(): GeneralForm {
    return this.addTuftProductForm.controls.generalForm;
  }

  public get optionalSettingsForm(): OptionalSettingsForm {
    return this.addTuftProductForm.controls.optionalSettingsForm;
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.urlParams = this.activatedRoute.snapshot.params;
    this.tuftProductToSave = TuftProduct.createEmptyTuftProduct();
    this.initialiseAddTuftProductComponent();
    this.subscribeToSaveEventEmitter();
    this.unsavedChangesNotifierService.getPartialStateOfUnsavedChangesNotifier();
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<NavigationTuftProductData>({tuftProduct: this.getCurrentTuftProduct()});
    this.unsavedChangesNotifierService.savePartialStateOfUnsavedChangesNotifier();
  }

  public saveTuftProduct(saveType: SaveType, navigateToSelectedRoute?: string): void {
    this.saveButtonTouched = true;
    const isValid = new FormValidationHelper().checkForms([this.generalForm, this.optionalSettingsForm], this.document);
    if (isValid && !this.tuftProductIsLocked) {
      const tuftProductToSave = this.getCurrentTuftProduct();
      this.saving = true;

      const request: Observable<void | number> = this.isEditingTuftProduct() ? this.tuftProductsService.update(tuftProductToSave) : this.tuftProductsService.save(tuftProductToSave);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving())).subscribe({
        next: (id: number) => {
          this.unsavedChangesNotifierService.notifyChangesToForm(false);
          this.textileService.navigateAndShowToast(
            saveType,
            TextileDataType.TUFT_PRODUCT,
            this.translationKeyForTuftProducts,
            this.isEditingTuftProduct(),
            tuftProductToSave.name,
            id,
            navigateToSelectedRoute
          );
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.isEditingTuftProduct() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  public deleteTuftProduct(): void {
    this.textileService.removeConfirmation(this.tuftProductToSave, TextileDataType.TUFT_PRODUCT, false, null, this.tuftProductsService);
  }

  public duplicateTuftProduct(): void {
    this.router.navigateByUrl(RouteUtils.paths.texStyle.tuftProduct.duplicateTuftProduct.absolutePath.replace(':id', this.tuftProductToSave.id.toString()));
  }

  public openConflictsDialog(): void {
    this.textileService.getConflicts(this.tuftProductToSave.id, this.tuftProductToSave.name, this.tuftProductsService, LastModifiedCardUtils.getTranslations(TextileDataType.TUFT_PRODUCT));
  }

  public isEditingTuftProduct(): boolean {
    return this.activatedRoute.snapshot.routeConfig.path === this.EDIT_TUFT_PRODUCT_PAGE_URL;
  }

  public unlockTuftProduct(): void {
    this.tuftProductIsLocked = false;

    this.generalForm.enable();
    this.optionalSettingsForm.enable();
    this.generalForm.controls.name.disable();
    this.generalForm.controls.version.patchValue(this.tuftProductToSave.version);
  }

  public compareWithGauge(gaugeA: Gauge, gaugeB: Gauge): boolean {
    return gaugeA?.id === gaugeB?.id;
  }

  public getActionText(): string {
    return this.translate.instant(this.isEditingTuftProduct() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', {
      object: this.translate.instant(this.translationKeyForTuftProducts, {count: 1}).toLowerCase()
    });
  }

  public onSelectedMenuItemChange(selectedMenuItem: TabsMenuItem): void {
    this.selectedMenuItem = selectedMenuItem;
  }

  public isGeneralMenuItemSelected(): boolean {
    return this.selectedMenuItem.value === AddTuftProductViewMode.GENERAL;
  }

  public isTuftProductBeingUsed(): boolean {
    return !!this.tuftProductToSave && this.tuftProductToSave.used && !this.isDuplicatingTuftProduct();
  }

  public canShowAddingTuftProduct(): boolean {
    return this.generalForm !== null && this.optionalSettingsForm !== null;
  }

  public navigateBack(): void {
    this.navigationHelperService.navigateToPreviousRoute(this.TUFT_PRODUCT_OVERVIEW_PAGE_URL);
  }

  public canShowInvalidFormMessageError(): boolean {
    const isGeneralFormInvalid = Object.values(this.generalForm.controls).some((control: FormControl) => control.invalid && control.touched);
    const isOptionalSettingsFormInvalid = Object.values(this.optionalSettingsForm.controls).some((control: FormControl) => control.invalid && control.touched);

    if (!isGeneralFormInvalid && !isOptionalSettingsFormInvalid) {
      this.saveButtonTouched = false;
    }

    const invalidForm = isGeneralFormInvalid || isOptionalSettingsFormInvalid;

    return invalidForm && this.saveButtonTouched;
  }

  public hasEditPermission(): boolean {
    return this.currentSubscription?.hasPermission(this.texstyleEditPermission);
  }

  public getVersion(): number {
    return !this.isEditingTuftProduct() ? 1 : this.tuftProductToSave.version;
  }

  public selectPrimaryBacking(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog(SelectWeaveProductComponent)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedWeaveProduct: OverviewListWeaveProduct) => {
        if (!!selectedWeaveProduct) {
          const selectedPrimaryBacking = WeaveProduct.fromOverviewListWeaveProduct(selectedWeaveProduct);
          this.generalForm.controls.primaryBacking.setValue(selectedPrimaryBacking);
          this.unsavedChangesNotifierService.notifyChangesToOthers();
        }
      });
  }

  public getLoadLink(): string {
    return ErrorHandlers.getLoadLink(RouteUtils.paths.texStyle.tuftProduct.editTuftProduct.absolutePath, this.generalForm.controls['name']);
  }

  private getGauges(): void {
    this.tuftProductsService
      .getGauges()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((gauges: Gauge[]) => (this.gauges = gauges));
  }

  private initialiseAddTuftProductComponent(): void {
    this.getGauges();
    this.setFormFields();
    this.setFormValues();
    this.setFormFieldsForMachine();
    this.navigationHelperService.setNewItemOrReopenDialogIfPresent(
      ({newItemId}: NavigationNewItemData) => this.setPrimaryBacking(newItemId),
      () => this.selectPrimaryBacking()
    );
  }

  private setFormFields(): void {
    const generalForm = this.formBuilder.group({
      name: this.formBuilder.control(null, Validators.required),
      version: this.formBuilder.control(1),
      widthInCm: this.formBuilder.control(null, [Validators.required, Validators.min(1)]),
      gauge: this.formBuilder.control(null, Validators.required),
      stitchRateInStitchesPerCm: this.formBuilder.control(null, [Validators.required, Validators.min(1)]),
      primaryBacking: this.formBuilder.control(null, Validators.required)
    });

    const optionalSettingsForm = this.formBuilder.group({
      pileHeightInCm: this.formBuilder.control(null, Validators.min(1)),
      color: this.formBuilder.control(null),
      blend: this.formBuilder.control(null),
      standardSpeed: this.formBuilder.control(null, Validators.min(1)),
      standardEfficiency: this.formBuilder.control(null, Validators.min(1)),
      weightInGramsPerMeter: this.formBuilder.control(null, Validators.min(1)),
      description: this.formBuilder.control(null),
      tuftingNotes: this.formBuilder.control(null),
      backingNotes: this.formBuilder.control(null)
    });

    this.addTuftProductForm = this.formBuilder.group({
      generalForm,
      optionalSettingsForm
    });

    this.setTuftProductNameAsyncValidator();
    this.menuItems[0].form = this.generalForm;
    this.unsavedChangesNotifierService.notifyChanges(this.generalForm, this.unSubscribeOnViewDestroy);

    this.menuItems[1].form = this.optionalSettingsForm;
    this.unsavedChangesNotifierService.notifyChanges(this.optionalSettingsForm, this.unSubscribeOnViewDestroy);
  }

  private setValues(tuftProductName: string = null): void {
    this.resetGeneralFormValues(tuftProductName);
    this.resetOptionalSettingsFormValues();
    this.setTuftProductNameAsyncValidator(tuftProductName);
  }

  private resetGeneralFormValues(tuftProductName: string): void {
    this.generalForm.reset({
      name: tuftProductName,
      version: this.tuftProductToSave.version,
      widthInCm: this.tuftProductToSave.widthInCm,
      gauge: this.tuftProductToSave.gauge,
      stitchRateInStitchesPerCm: this.tuftProductToSave.stitchRateInStitchesPerCm,
      primaryBacking: this.tuftProductToSave.primaryBacking
    });
  }

  private resetOptionalSettingsFormValues(): void {
    this.optionalSettingsForm.reset({
      pileHeightInCm: this.tuftProductToSave.pileHeightInCm,
      color: this.tuftProductToSave.color,
      blend: this.tuftProductToSave.blend,
      standardSpeed: this.tuftProductToSave.standardSpeed,
      standardEfficiency: this.tuftProductToSave.standardEfficiency,
      weightInGramsPerMeter: this.tuftProductToSave.weightInGramsPerMeter,
      description: this.tuftProductToSave.description,
      tuftingNotes: this.tuftProductToSave.tuftingNotes,
      backingNotes: this.tuftProductToSave.backingNotes
    });
  }

  private getCurrentTuftProduct(): TuftProduct {
    if (AssertionUtils.isNullOrUndefined(this.generalForm)) {
      return null;
    }

    const name: string = this.generalForm.getRawValue().name;
    const version: number = this.generalForm.value.version;
    const widthInCm: number = this.generalForm.value.widthInCm;
    const gauge: Gauge = this.generalForm.getRawValue().gauge;
    const stitchRateInStitchesPerCm: number = this.generalForm.value.stitchRateInStitchesPerCm;
    const primaryBacking: WeaveProduct = this.generalForm.value.primaryBacking;
    const pileHeightInCm: number = this.optionalSettingsForm.value.pileHeightInCm;
    const color: string = this.optionalSettingsForm.value.color;
    const blend: string = this.optionalSettingsForm.value.blend;
    const standardSpeed: number = this.optionalSettingsForm.value.standardSpeed;
    const standardEfficiency: number = this.optionalSettingsForm.value.standardEfficiency;
    const weightInGramsPerMeter: number = this.optionalSettingsForm.value.weightInGramsPerMeter;
    const description: string = this.optionalSettingsForm.value.description;
    const tuftingNotes: string = this.optionalSettingsForm.value.tuftingNotes;
    const backingNotes: string = this.optionalSettingsForm.value.backingNotes;

    const used = this.isEditingTuftProduct() && this.tuftProductToSave.used;

    return new TuftProduct(
      this.isEditingTuftProduct() ? this.tuftProductToSave.id : null,
      name,
      version,
      description,
      color,
      blend,
      widthInCm,
      gauge,
      stitchRateInStitchesPerCm,
      pileHeightInCm,
      weightInGramsPerMeter,
      primaryBacking,
      standardSpeed,
      standardEfficiency,
      tuftingNotes,
      backingNotes,
      used
    );
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {
        object: this.translate.instant(this.translationKeyForTuftProducts, {
          count: 1
        })
      }),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }

  private setTuftProductNameAsyncValidator(name: string = null): void {
    this.generalForm.controls.name.setAsyncValidators([AsyncUniqueValidator.createValidator(this.tuftProductsService, name)]);
  }

  private isDuplicatingTuftProduct(): boolean {
    return this.activatedRoute.snapshot.routeConfig.path === this.DUPLICATE_TUFT_PRODUCT_PAGE_URL;
  }

  private disableForm(): void {
    if (!this.hasEditPermission() || this.tuftProductIsLocked) {
      this.generalForm.disable();
      this.optionalSettingsForm.disable();
    }

    if (this.isEditingTuftProduct()) {
      this.generalForm.controls.name.disable();
    }
  }

  private setFormValues(): void {
    const emptyTuftProductState = {tuftProduct: null} as NavigationTuftProductData;
    const tuftProductState = this.navigationHelperService.getPartialState<NavigationTuftProductData>(Object.keys(emptyTuftProductState));

    if (!AssertionUtils.isNullOrUndefined(tuftProductState) && !ObjectUtils.isDeepEqual(tuftProductState, emptyTuftProductState)) {
      this.tuftProductToSave = tuftProductState.tuftProduct;
      this.setTuftProduct();
    } else if (!!this.urlParams.id) {
      this.tuftProductsService
        .getById(parseInt(this.urlParams.id, 10))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe({
          next: (tuftProduct: TuftProduct) => {
            this.tuftProductToSave = tuftProduct;
            this.setTuftProduct();
          },
          error: ErrorHandlers.navigateToOverviewAndThrowError(this.router, this.TUFT_PRODUCT_OVERVIEW_PAGE_URL, this.navigationHelperService)
        });
    } else {
      this.setValues();
      this.isLoadingTuftProduct = false;
    }
  }

  private setTuftProduct(): void {
    this.isLoadingTuftProduct = false;
    this.tuftProductIsLocked = this.isEditingTuftProduct() && this.tuftProductToSave.used;
    this.setValues(this.isDuplicatingTuftProduct() ? null : this.tuftProductToSave.name);
    this.disableForm();
  }

  private setFormFieldsForMachine(): void {
    const emptyMachineOverviewState = {equipmentId: null} as NavigationMachineOverviewData;
    const machineOverviewState = this.navigationHelperService.getPartialState<NavigationMachineOverviewData>(Object.keys(emptyMachineOverviewState), true);

    if (!AssertionUtils.isNullOrUndefined(machineOverviewState) && !ObjectUtils.isDeepEqual(machineOverviewState, emptyMachineOverviewState)) {
      this.machines
        .getById(machineOverviewState.equipmentId)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((tuftMachine: TuftMachine) => {
          this.maxWidthInCm = tuftMachine.tuftingMachineType.width;
          this.generalForm.controls.widthInCm.addValidators(Validators.max(this.maxWidthInCm));
          this.generalForm.controls.widthInCm.updateValueAndValidity();
          this.generalForm.controls.gauge.setValue(tuftMachine.tuftingMachineType.gauge);
          this.generalForm.controls.gauge.disable();
        });
    }
  }

  private setPrimaryBacking(id: number): void {
    this.weaveProducts
      .getAll()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((weaveProducts: OverviewListWeaveProduct[]) =>
        this.generalForm.controls.primaryBacking.setValue(WeaveProduct.fromOverviewListWeaveProduct(weaveProducts.find((weaveProduct: OverviewListWeaveProduct) => weaveProduct.id === id)))
      );
  }

  private subscribeToSaveEventEmitter(): void {
    this.unsavedChangesNotifierService
      .getSaveEventEmitter()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((res: {action: boolean; navigateToSelectedRoute: string}) => {
        if (res) {
          this.saveTuftProduct(SaveType.SAVE_AND_CLOSE, res.navigateToSelectedRoute);
        }
      });
  }
}
